/**
 * Client-side Prometheus text parser (ADR-TS-0014 — parse-only). Reads the bounded
 * ADR-TS-0013 contract; ignores unknown series gracefully (NFR-TS-013 degradation).
 */

export interface ParsedBank {
  readonly bank: string;
  concurrency: number;
  throughputRps: number;
  latencyP95: number;
  errorRate: number;
  health: number; // 0 healthy, 1 degraded, 2 unreachable
  breaker: number; // 0 armed, 1 throttling, 2 halted
  envelope: { maxConcurrency: number; maxRate: number };
  readonly requests: Map<string, number>; // `${scenario_class}:${outcome}` -> count
}

export interface ParsedSnapshot {
  readonly totalConcurrency: number;
  readonly aggregateTps: number;
  readonly invariantBreaches: number;
  readonly banks: ReadonlyArray<ParsedBank>;
  readonly idle: boolean;
  /** Recognised series names seen in the exposition (for the FR-TS-094 contract check). */
  readonly knownSeriesSeen: ReadonlySet<string>;
}

/** The exact series set the dashboard may depend on (ADR-TS-0013 / FR-TS-094). */
export const KNOWN_SERIES: ReadonlySet<string> = new Set([
  'ts_run_concurrency',
  'ts_total_concurrency',
  'ts_aggregate_tps',
  'ts_throughput_requests_per_second',
  'ts_latency_p95_seconds',
  'ts_error_rate_ratio',
  'ts_requests_total',
  'ts_target_health',
  'ts_circuit_breaker_state',
  'ts_envelope_limit',
  'ts_invariant_breaches_total',
]);

const VALUE_RE = /^(?:[-+]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][-+]?\d+)?|NaN|\+Inf|-Inf)$/;
const LINE_RE = /^([a-zA-Z_:][a-zA-Z0-9_:]*)(?:\{([^}]*)\})?\s+(\S+)$/;
const LABEL_RE = /([a-zA-Z_][a-zA-Z0-9_]*)="((?:[^"\\]|\\.)*)"/g;

function parseLabels(raw: string): Map<string, string> {
  const out = new Map<string, string>();
  if (!raw) return out;
  LABEL_RE.lastIndex = 0;
  let lm: RegExpExecArray | null;
  while ((lm = LABEL_RE.exec(raw)) !== null) {
    const k = lm[1] ?? '';
    const v = lm[2] ?? '';
    if (k) {
      out.set(k, v.replace(/\\"/g, '"').replace(/\\n/g, '\n').replace(/\\\\/g, '\\'));
    }
  }
  return out;
}

function toNum(tok: string): number {
  if (tok === 'NaN' || tok === '+Inf' || tok === '-Inf') return 0;
  const n = Number(tok);
  return Number.isFinite(n) ? n : 0;
}

function blankBank(bank: string): ParsedBank {
  return {
    bank,
    concurrency: 0,
    throughputRps: 0,
    latencyP95: 0,
    errorRate: 0,
    health: 0,
    breaker: 0,
    envelope: { maxConcurrency: 0, maxRate: 0 },
    requests: new Map(),
  };
}

export function parseMetrics(text: string): ParsedSnapshot {
  const banks = new Map<string, ParsedBank>();
  const known = new Set<string>();
  const get = (id: string): ParsedBank => {
    let b = banks.get(id);
    if (!b) {
      b = blankBank(id);
      banks.set(id, b);
    }
    return b;
  };
  let totalConcurrency = 0;
  let aggregateTps = 0;
  let invariantBreaches = 0;

  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    if (!line || line.startsWith('#')) continue;
    const m = LINE_RE.exec(line);
    if (!m) continue;
    const name = m[1] ?? '';
    const labelsRaw = m[2] ?? '';
    const valueTok = m[3] ?? '';
    if (!KNOWN_SERIES.has(name)) continue;
    if (!VALUE_RE.test(valueTok)) continue;
    known.add(name);
    const labels = parseLabels(labelsRaw);
    const value = toNum(valueTok);
    const bank = labels.get('bank') ?? '';
    switch (name) {
      case 'ts_run_concurrency':
        if (bank) get(bank).concurrency = value;
        break;
      case 'ts_throughput_requests_per_second':
        if (bank) get(bank).throughputRps = value;
        break;
      case 'ts_latency_p95_seconds':
        if (bank) get(bank).latencyP95 = value;
        break;
      case 'ts_error_rate_ratio':
        if (bank) get(bank).errorRate = value;
        break;
      case 'ts_target_health':
        if (bank) get(bank).health = value;
        break;
      case 'ts_circuit_breaker_state':
        if (bank) get(bank).breaker = value;
        break;
      case 'ts_envelope_limit': {
        if (bank) {
          const kind = labels.get('kind');
          if (kind === 'max_concurrency') get(bank).envelope.maxConcurrency = value;
          else if (kind === 'max_rate') get(bank).envelope.maxRate = value;
        }
        break;
      }
      case 'ts_requests_total': {
        const sc = labels.get('scenario_class') ?? '';
        const outc = labels.get('outcome') ?? '';
        if (bank && sc && outc) get(bank).requests.set(`${sc}:${outc}`, value);
        break;
      }
      case 'ts_total_concurrency':
        totalConcurrency = value;
        break;
      case 'ts_aggregate_tps':
        aggregateTps = value;
        break;
      case 'ts_invariant_breaches_total':
        invariantBreaches = value;
        break;
      default:
        break;
    }
  }

  const idle = totalConcurrency <= 0 && aggregateTps <= 0;
  return {
    totalConcurrency,
    aggregateTps,
    invariantBreaches,
    banks: [...banks.values()],
    idle,
    knownSeriesSeen: known,
  };
}
